import {
  getBrowserNotificationBadgeUrl,
  getBrowserNotificationIconUrl
} from "../constants/browserNotifications";
import { TranslationService } from "./TranslationService";

/**
 * Servicio para manejar notificaciones del navegador
 * Permite mostrar notificaciones emergentes incluso cuando el usuario está en otra pestaña
 */
export class BrowserNotificationService {
  /**
   * Verifica si el navegador soporta notificaciones
   */
  public static isSupported(): boolean {
    return typeof window !== "undefined" && "Notification" in window;
  }

  /**
   * Solicita permiso al usuario para mostrar notificaciones
   * Nota: Si el permiso ya está en 'granted' o 'denied', el navegador no mostrará el diálogo
   * y simplemente devolverá el estado actual
   */
  public static async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      console.warn("Las notificaciones no están soportadas en este navegador");
      return "denied";
    }

    const currentPermission = Notification.permission;

    // Si ya está concedido o denegado, el navegador no mostrará el diálogo
    if (currentPermission !== "default") {
      return currentPermission;
    }

    try {
      const permission = await Notification.requestPermission();
      return permission;
    } catch (error) {
      console.error("Error al solicitar permiso de notificaciones:", error);
      return "denied";
    }
  }

  /**
   * Obtiene el estado actual del permiso
   */
  public static getPermission(): NotificationPermission {
    if (!this.isSupported()) {
      return "denied";
    }
    return Notification.permission;
  }

  /**
   * Verifica si se tienen permisos para mostrar notificaciones
   */
  public static hasPermission(): boolean {
    return this.getPermission() === "granted";
  }

  /**
   * Muestra una notificación del navegador (icono Intiza vía URLs absolutas en public/)
   */
  public static async showNotification(
    title: string,
    options?: NotificationOptions
  ): Promise<void> {
    if (!this.hasPermission()) {
      console.warn("No se tienen permisos para mostrar notificaciones");
      return;
    }

    const iconUrl = getBrowserNotificationIconUrl();
    const badgeUrl = getBrowserNotificationBadgeUrl();
    const defaults: NotificationOptions = {
      icon: iconUrl || undefined,
      badge: badgeUrl || undefined
    };

    try {
      if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.ready;
        if (registration.active) {
          await registration.showNotification(title, {
            ...defaults,
            ...options
          });
          return;
        }
      }

      new Notification(title, {
        ...defaults,
        ...options
      });
    } catch (error) {
      console.error("Error al mostrar notificación:", error);
    }
  }

  /**
   * Muestra una notificación con datos adicionales
   */
  public static async showNotificationWithData(
    title: string,
    body: string,
    data?: any,
    tag?: string
  ): Promise<void> {
    return this.showNotification(title, {
      body,
      tag: tag || title,
      data,
      requireInteraction: false,
      silent: false
    });
  }

  /**
   * Notificación de confirmación que se muestra justo después de conceder el permiso
   */
  public static async showPermissionGrantedNotification(): Promise<void> {
    // Pequeña espera para que el navegador termine de cerrar el diálogo de permiso
    await new Promise((resolve) => setTimeout(resolve, 500));
    await this.showNotificationWithData(
      TranslationService.translate.BrowserNotifyPermissionGrantedTitle,
      TranslationService.translate.BrowserNotifyPermissionGrantedBody,
      { type: "permission-granted" }
    );
  }
}
